import {
  Component,
  computed,
  ElementRef,
  HostListener,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export interface DateRange {
  from: string;
  to: string;
}

@Component({
  selector: 'app-date-range-picker',
  imports: [NgClass],
  templateUrl: './date-range-picker.html',
})
export class DateRangePickerComponent implements OnInit, OnDestroy {
  dateFrom = input<string>('');
  dateTo = input<string>('');
  placeholder = input<string>('Select Date Range');
  variant = input<'default' | 'compact'>('default');
  singleDate = input<boolean>(false);
  error = input<boolean>(false);
  rangeChange = output<DateRange>();

  private static readonly DROPDOWN_HEIGHT = 330;

  protected open = signal(false);
  protected dropdownPos = signal<{
    top?: string;
    bottom?: string;
    right?: string;
    left?: string;
  } | null>(null);
  protected viewMonth = signal(new Date().getMonth());
  protected viewYear = signal(new Date().getFullYear());
  protected rightViewMonth = signal(ahead(new Date().getMonth(), new Date().getFullYear()).m);
  protected rightViewYear = signal(ahead(new Date().getMonth(), new Date().getFullYear()).y);
  protected hoverDate = signal<Date | null>(null);
  protected pendingFrom = signal<Date | null>(null);

  protected readonly DAY_HEADERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  protected showLeftYearDropdown = signal(false);
  protected showRightYearDropdown = signal(false);
  protected leftYearPageStart = signal(0);
  protected rightYearPageStart = signal(0);
  protected leftYearRange = computed(() =>
    Array.from({ length: 12 }, (_, i) => this.leftYearPageStart() + i),
  );
  protected rightYearRange = computed(() =>
    Array.from({ length: 12 }, (_, i) => this.rightYearPageStart() + i),
  );

  constructor(private el: ElementRef) {}

  private computePosition(): void {
    const rect = (this.el.nativeElement as HTMLElement).getBoundingClientRect();
    const h = DateRangePickerComponent.DROPDOWN_HEIGHT;
    const spaceBelow = window.innerHeight - rect.bottom;
    const up = spaceBelow < h && rect.top > spaceBelow;

    const CALENDAR_MIN_WIDTH = 248;
    const rightValue = Math.max(8, window.innerWidth - rect.right);
    const horizontal =
      window.innerWidth - rightValue - CALENDAR_MIN_WIDTH >= 8
        ? { right: `${rightValue}px` }
        : { left: `${Math.max(8, rect.left)}px` };

    if (up) {
      const rawBottom = window.innerHeight - rect.top + 6;
      this.dropdownPos.set({ bottom: `${Math.max(8, rawBottom)}px`, ...horizontal });
    } else {
      const rawTop = rect.bottom + 6;
      this.dropdownPos.set({
        top: `${Math.min(rawTop, window.innerHeight - h - 8)}px`,
        ...horizontal,
      });
    }
  }

  private reposition = (): void => {
    if (!this.open()) return;
    this.computePosition();
  };

  ngOnInit(): void {
    window.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
    // Capture phase: modals stop click propagation, so the document:click listener below never
    // sees outside clicks there. That listener stays for closing the year dropdowns.
    document.addEventListener('pointerdown', this.onOutsidePointerDown, true);
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
    document.removeEventListener('pointerdown', this.onOutsidePointerDown, true);
  }

  private onOutsidePointerDown = (e: PointerEvent): void => {
    if (this.open() && !this.el.nativeElement.contains(e.target as Node)) {
      this.open.set(false);
      this.pendingFrom.set(null);
    }
  };

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (!this.el.nativeElement.contains(e.target as Node)) {
      this.open.set(false);
      this.pendingFrom.set(null);
    }
    this.showLeftYearDropdown.set(false);
    this.showRightYearDropdown.set(false);
  }

  protected toggle(): void {
    if (!this.open()) this.computePosition();
    this.open.update((v) => !v);
  }

  protected get label(): string {
    const f = this.dateFrom(),
      t = this.dateTo();
    if (!f) return this.placeholder();
    return t ? `${fmt(f)} – ${fmt(t)}` : fmt(f);
  }

  protected leftMonthLabel = computed(() => MONTHS[this.viewMonth()]);
  protected leftLabel = computed(() => `${MONTHS[this.viewMonth()]} ${this.viewYear()}`);
  protected rightMonthLabel = computed(() => MONTHS[this.rightViewMonth()]);
  protected rightYearLabel = computed(() => this.rightViewYear());
  protected rightLabel = computed(() => `${MONTHS[this.rightViewMonth()]} ${this.rightViewYear()}`);
  protected leftWeeks = computed(() => weeks(this.viewYear(), this.viewMonth()));
  protected rightWeeks = computed(() => weeks(this.rightViewYear(), this.rightViewMonth()));

  protected prev(): void {
    const p = behind(this.viewMonth(), this.viewYear());
    this.viewMonth.set(p.m);
    this.viewYear.set(p.y);
  }

  protected next(): void {
    const n = ahead(this.rightViewMonth(), this.rightViewYear());
    this.rightViewMonth.set(n.m);
    this.rightViewYear.set(n.y);
  }

  protected nextLeft(): void {
    const n = ahead(this.viewMonth(), this.viewYear());
    this.viewMonth.set(n.m);
    this.viewYear.set(n.y);
  }

  protected toggleLeftYear(): void {
    const open = !this.showLeftYearDropdown();
    this.showLeftYearDropdown.set(open);
    this.showRightYearDropdown.set(false);
    if (open) this.leftYearPageStart.set(Math.floor(this.viewYear() / 12) * 12);
  }

  protected toggleRightYear(): void {
    const open = !this.showRightYearDropdown();
    this.showRightYearDropdown.set(open);
    this.showLeftYearDropdown.set(false);
    if (open) this.rightYearPageStart.set(Math.floor(this.rightViewYear() / 12) * 12);
  }

  protected selectLeftYear(year: number): void {
    this.viewYear.set(year);
    this.showLeftYearDropdown.set(false);
  }

  protected selectRightYear(year: number): void {
    if (year < this.viewYear()) return;
    this.rightViewYear.set(year);
    this.showRightYearDropdown.set(false);
  }

  protected isDisabled(date: Date): boolean {
    const p = this.pendingFrom();
    return !!p && date < p && !sameDay(date, p);
  }

  protected click(date: Date): void {
    if (this.isDisabled(date)) return;
    if (this.singleDate()) {
      this.rangeChange.emit({ from: iso(date), to: '' });
      this.open.set(false);
      return;
    }
    const p = this.pendingFrom();
    if (!p) {
      this.pendingFrom.set(date);
      this.rangeChange.emit({ from: iso(date), to: '' });
    } else {
      this.pendingFrom.set(null);
      this.hoverDate.set(null);
      this.rangeChange.emit({ from: iso(p), to: iso(date) });
      this.open.set(false);
    }
  }

  protected hover(date: Date): void {
    this.hoverDate.set(date);
  }
  protected leave(): void {
    this.hoverDate.set(null);
  }

  protected state(date: Date) {
    const p = this.pendingFrom();
    let s: Date | null, e: Date | null;

    if (p) {
      s = p;
      const h = this.hoverDate();
      e = h && !sameDay(h, p) ? (h > p ? h : null) : null;
      if (h && h < p) {
        s = h;
        e = p;
      }
    } else {
      s = this.dateFrom() ? parse(this.dateFrom()) : null;
      e = this.dateTo() ? parse(this.dateTo()) : null;
    }

    return {
      isStart: !!s && sameDay(date, s),
      isEnd: !!e && sameDay(date, e),
      inRange: !!s && !!e && date > s && date < e,
      isToday: sameDay(date, new Date()),
      hasRange: !!s && !!e,
    };
  }

  protected reset(): void {
    this.pendingFrom.set(null);
    this.rangeChange.emit({ from: '', to: '' });
    this.open.set(false);
  }
}

function weeks(y: number, m: number): (Date | null)[][] {
  const offset = new Date(y, m, 1).getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const cells: (Date | null)[] = Array(offset).fill(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(y, m, d));
  while (cells.length % 7) cells.push(null);
  const rows: (Date | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
  return rows;
}

function ahead(m: number, y: number) {
  return m === 11 ? { m: 0, y: y + 1 } : { m: m + 1, y };
}
function behind(m: number, y: number) {
  return m === 0 ? { m: 11, y: y - 1 } : { m: m - 1, y };
}
function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function parse(s: string) {
  return new Date(s + 'T00:00:00');
}
function iso(d: Date) {
  return d.toISOString().split('T')[0];
}
function fmt(s: string) {
  return parse(s).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });
}
